import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getScoutDashboard, fetchFollowedTeams, fetchTeamRoster, loadMoreTeamRoster, setSelectedTeam, clearTeamRoster, unfollowPlayer, unfollowTeam } from '../../features/scout/scoutProfileSlice';
import { WelcomeBanner } from './dashboard/WelcomeBanner';
import { PipelineCard } from './dashboard/PipelineCard';
import { FollowPlayersCard } from './dashboard/FollowPlayersCard';
import { FollowedProfiles } from './dashboard/FollowedProfiles';
import { StatsSection } from './dashboard/StatsSection';
import { ScoutSidebar } from './dashboard/ScoutSidebar';
import { currentSeasonYear } from '../../utils/season';

export const ScoutDashboard = () => {
  const { activeView } = useOutletContext();
  const dispatch = useDispatch();
  const {
    profile,
    followedPlayers,
    topPlayers,
    dashboardLoading,
    followedTeams,
    followedTeamsLoading,
    teamRoster,
    teamRosterLoading,
    selectedTeam,
    teamRosterPagination
  } = useSelector((state) => state.scoutProfile);
  const [activeProfileTab, setActiveProfileTab] = useState('players'); // 'players' or 'teams'

  useEffect(() => {
    // Fetch scout dashboard data on mount
    dispatch(getScoutDashboard({ page: 1, limit: 20 }));
    // Fetch followed teams
    dispatch(fetchFollowedTeams({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Fetch team roster when a team is selected
  useEffect(() => {
    if (selectedTeam?._id) {
      dispatch(fetchTeamRoster({ teamId: selectedTeam._id, page: 1, limit: 50, seasonYear: currentSeasonYear() }));
    }
  }, [selectedTeam, dispatch]);

  const handleLoadMoreRoster = () => {
    if (selectedTeam?._id && teamRosterPagination.hasMore && !teamRosterLoading) {
      const nextPage = teamRosterPagination.currentPage + 1;
      dispatch(loadMoreTeamRoster({
        teamId: selectedTeam._id,
        page: nextPage,
        limit: 50,
        seasonYear: currentSeasonYear()
      }));
    }
  };

  const handleTabChange = (tab) => {
    setActiveProfileTab(tab);
    if (tab === 'players') {
      dispatch(clearTeamRoster());
    }
  };

  const handleTeamSelect = (team) => {
    dispatch(setSelectedTeam(team));
  };

  const handleUnfollowPlayer = async (playerId) => {
    try {
      await dispatch(unfollowPlayer(playerId)).unwrap();
      // Refetch dashboard data to update followed players list
      dispatch(getScoutDashboard({ page: 1, limit: 20 }));
    } catch (error) {
      console.error('Failed to unfollow player:', error);
    }
  };

  const handleUnfollowTeam = async (teamId) => {
    try {
      await dispatch(unfollowTeam(teamId)).unwrap();
      // Refetch followed teams list
      dispatch(fetchFollowedTeams({ page: 1, limit: 100 }));
      // If the unfollowed team was selected, clear the roster
      if (selectedTeam?._id === teamId) {
        dispatch(clearTeamRoster());
      }
    } catch (error) {
      console.error('Failed to unfollow team:', error);
    }
  };

  return (
    <>
      {/* Top Section - Welcome & Cards - Full Width */}
      {/* <div className="border border-slate-700/50 rounded-2xl grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <WelcomeBanner />
        <PipelineCard />
        <FollowPlayersCard />
      </div> */}

      {/* Grid with Sidebar and Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar */}
        <div className="lg:col-span-1">
          <ScoutSidebar activeView={activeView} />
        </div>

        {/* Right Content Area */}
        <div className="lg:col-span-3 border border-slate-700/50 rounded-2xl">
          <FollowedProfiles
            followedPlayers={followedPlayers}
            followedTeams={followedTeams}
            topPlayers={topPlayers}
            loading={dashboardLoading}
            teamsLoading={followedTeamsLoading}
            onTeamSelect={handleTeamSelect}
            onTabChange={handleTabChange}
            selectedTeam={selectedTeam}
            onUnfollowPlayer={handleUnfollowPlayer}
            onUnfollowTeam={handleUnfollowTeam}
          />
          <StatsSection
            hasFollowedPlayers={followedPlayers && followedPlayers.length > 0}
            followedPlayers={followedPlayers}
            activeProfileTab={activeProfileTab}
            teamRoster={teamRoster}
            teamRosterLoading={teamRosterLoading}
            selectedTeam={selectedTeam}
            teamRosterPagination={teamRosterPagination}
            onLoadMoreRoster={handleLoadMoreRoster}
          />
        </div>
      </div>
    </>
  );
};

export default ScoutDashboard;
